// Build pipeline injects NEXT_PUBLIC_DEPLOY_ENV=dev|prod (from terraform
// env_level); local dev has no env var.
const RAW_DEPLOY_ENV = process.env.NEXT_PUBLIC_DEPLOY_ENV;
export const DEPLOY_ENV: 'development' | 'production' =
  RAW_DEPLOY_ENV === 'prod' || RAW_DEPLOY_ENV === 'production' ? 'production' : 'development';

/** No env var, which the pipeline always sets — a machine someone is working on, which `DEPLOY_ENV`
 *  folds together with a deployed `dev`. */
export const IS_LOCAL_ENV = RAW_DEPLOY_ENV === undefined;

export const IS_PRODUCTION = process.env.NODE_ENV === 'production';

export const FORCE_ANALYTICS_ENABLED = process.env.NEXT_PUBLIC_FORCE_ANALYTICS === 'true';

export const IS_TRACKING_ENABLED = IS_PRODUCTION || FORCE_ANALYTICS_ENABLED;

export const BAZAK_URL = 'https://bazak.ai';

export const STATIC_CDN_URL = process.env.NEXT_PUBLIC_STATIC_CDN_URL || '';

export const POSTHOG_HOST = 'https://us.i.posthog.com';

// The management API's host and project id are shared with apps/platform, which links into the
// PostHog app with the same pair.
export { POSTHOG_API_HOST, POSTHOG_PROJECT_ID } from '@bazak/analytics/constants';

export function getPostHogBootstrapCookieName(): string {
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  return apiKey ? `ph_${apiKey}_bootstrap` : 'ph_bootstrap';
}

/** posthog-js's persistence key — it names both the localStorage entry and the cookie. Not in
 *  `lib/utils/posthog-server.ts` because client code reads it too, and that module pulls in
 *  `@bazak/analytics/server`. */
export function getPostHogPersistenceKey(): string {
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  return apiKey ? `ph_${apiKey}_posthog` : '';
}

// Lives in its own module because this one reads `process.env` at module scope and MCP widgets
// need the constant. Anything else a widget needs belongs there too, not here.
export { DEFAULT_TIMEZONE } from './timezone';

export const MAX_MESSAGE_LENGTH = 256;

export const MAX_WELCOME_SUGGESTIONS = 6;

/**
 * Window during which a second composer-send attempt is treated as a
 * duplicate of the first. Sized to cover both:
 *  1. Android Chrome ghost-clicks — PostHog runtime evidence (issue
 *     019e1855) showed every offending pair fired within 1–66ms; 300ms
 *     gives ~4.5× safety margin while staying well below human "two
 *     distinct messages" cadence.
 *  2. The brief ~50–200ms window before assistant-ui's reactive
 *     `disabled` prop (driven by `isRunning`) propagates after the
 *     first send is dispatched.
 *
 * 300ms is also the historical iOS double-tap-to-zoom threshold.
 */
export const COMPOSER_GHOST_CLICK_DEDUP_MS = 300;

/**
 * Sibling of the above for the TTS play button, where a second tap lands before
 * React re-renders the button and restarts playback from the first word. Wider
 * than the send window because the cost of swallowing a tap is only a missed
 * replay, not a lost message — an explicit stop resets it.
 */
export const TTS_DOUBLE_TAP_MS = 500;

export const SUPPORTED_LOCALES = [
  'en',
  'de',
  'he',
  'es',
  'nl',
  'it',
  'fr',
  'pl',
  'ru',
  'zh',
  'ar',
  'pt',
] as const;

export const LOCAL_THREAD_ID_PREFIX = '__LOCALID_';

export const NEW_CHAT_THREAD_ID = 'new';

export const WIDGET_VERSION = '64';

// Only safe for content-addressed assets: the filename is the cache key for a year.
export const IMMUTABLE_CACHE = 'public, max-age=31536000, immutable';

export const LINK_NAVIGATION_MODE = {
  NEW_TAB: 'new-tab',
  REPLACE_PARENT: 'replace-parent',
} as const;

export const EMBED_CONTEXT = {
  INLINE: 'inline',
} as const;

// Permissions required for embedded iframe functionality
// Used by: next.config.ts (Permissions-Policy header) and widget-core.js (iframe.allow attribute)
export const IFRAME_PERMISSIONS = ['clipboard-write', 'storage-access', 'microphone'] as const;

// Tracks the last host-derived locale we attempted to switch to. Stops
// `useHostLanguageDetection` from infinite-reloading when the `preferred-locale`
// cookie cannot persist (e.g. third-party storage partitioning in cross-site
// iframes).
export const HOST_LOCALE_DETECTION_ATTEMPTED_KEY = 'host-locale-detection-attempted';
export const PENDING_LANGUAGE_SWITCH_SEND_KEY = 'pending-language-switch-send';
